import React, { useState, useEffect } from 'react';
import { 
  getPartyListAPI, 
  getTokenListAPI, 
  getInternalAccountListAPI, 
  getPaymentModeListAPI, 
  getPartyAccountListAPI,
  getDollarAccountListAPI,
  Party, 
  Token, 
  InternalAccount, 
  PaymentMode, 
  PartyAccount,
  DollarAccount
} from '../../../../services/adminApi';
import styles from './TransactionForm.module.css';

interface AddSellTransactionFormProps {
  onSubmit: (transactionData: SellTransactionFormData) => void;
  onCancel: () => void;
  initialData?: Partial<SellTransactionFormData>;
}

export interface SellTransactionFormData {
  date: string;
  partyId: string;
  partyName: string;
  mobile: string;
  address: string;
  tokenId: string;
  tokenName: string;
  payIn: string;
  quantity: number;
  tokenRate: number;
  dollarRate: number;
  total: number;
  paymentModeId: string;
  paymentMode: string;
  fromAccountId: string;
  fromAccount: string;
  partyAccountId: string;
  partyAccount: string;
  referenceNo: string;
  paymentDate: string;
  remarks?: string;
}

const AddSellTransactionForm: React.FC<AddSellTransactionFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
}) => {
  const [formData, setFormData] = useState<SellTransactionFormData>({
    date: new Date().toISOString().split('T')[0],
    partyId: '',
    partyName: '',
    mobile: '',
    address: '',
    tokenId: '',
    tokenName: '',
    payIn: 'INR',
    quantity: 0,
    tokenRate: 1,
    dollarRate: 83,
    total: 0,
    paymentModeId: '',
    paymentMode: '',
    fromAccountId: '',
    fromAccount: '',
    partyAccountId: '',
    partyAccount: '',
    referenceNo: '',
    paymentDate: new Date().toISOString().split('T')[0],
    remarks: '',
  });

  const [parties, setParties] = useState<Party[]>([]);
  const [tokens, setTokens] = useState<Token[]>([]);
  const [accounts, setAccounts] = useState<InternalAccount[]>([]);
  const [dollarAccounts, setDollarAccounts] = useState<DollarAccount[]>([]);
  const [paymentModes, setPaymentModes] = useState<PaymentMode[]>([]);
  const [partyAccounts, setPartyAccounts] = useState<PartyAccount[]>([]);
  const [filteredPartyAccounts, setFilteredPartyAccounts] = useState<PartyAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load dropdown data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [partiesResponse, tokensResponse, accountsResponse, dollarAccountsResponse, paymentModesResponse] = await Promise.all([
          getPartyListAPI({ pageNo: 1, pageSize: 100 }),
          getTokenListAPI({ pageNo: 1, pageSize: 100 }),
          getInternalAccountListAPI({ pageNo: 1, pageSize: 100 }),
          getDollarAccountListAPI({ pageNo: 1, pageSize: 100 }),
          getPaymentModeListAPI({ pageNo: 1, pageSize: 100 }),
        ]);
        setParties((partiesResponse.data || []).filter(p => p.isActive));
        setTokens((tokensResponse.data || []).filter(t => t.isActive));
        setAccounts((accountsResponse.data || []).filter(a => a.isActive));
        setDollarAccounts((dollarAccountsResponse.data || []).filter(da => da.isActive));
        setPaymentModes((paymentModesResponse.data || []).filter(pm => pm.isActive));
        // Load all party accounts with partyId: 0 to get all accounts
        const partyAccountsResponse = await getPartyAccountListAPI({ partyId: 0, accountTypeId: 0, pageNo: 1, pageSize: 1000 });
        setPartyAccounts((partyAccountsResponse.data || []).filter(pa => pa.isActive));
      } catch (error) {
        console.error('Failed to load form data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Populate form with initial data when editing
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  // Filter party accounts when party or payment mode changes
  useEffect(() => {
    if (formData.partyId) {
      const partyIdNum = parseInt(formData.partyId);
      let filtered = partyAccounts.filter(pa => {
        // Handle both number and string comparisons
        return pa.partyId === partyIdNum || pa.partyId?.toString() === formData.partyId;
      });

      // If payment mode is selected, filter by payment mode ID matching account type ID
      if (formData.paymentModeId) {
        const paymentModeIdNum = parseInt(formData.paymentModeId);
        
        // Filter accounts by payment mode ID matching account type ID
        filtered = filtered.filter(pa => {
          return pa.accountTypeId === paymentModeIdNum;
        });
      }

      setFilteredPartyAccounts(filtered);
    } else {
      setFilteredPartyAccounts([]);
    }
  }, [formData.partyId, formData.paymentModeId, partyAccounts]);

  // Calculate total when values change
  useEffect(() => {
    const total = formData.quantity * formData.tokenRate * formData.dollarRate;
    setFormData((prev) => ({
      ...prev,
      total,
    }));
  }, [formData.quantity, formData.tokenRate, formData.dollarRate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    
    // If payIn changes, update dollarRate based on the new value
    if (name === 'payIn') {
      if (value === 'USD' || value === 'Token') {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          dollarRate: 1, // Set to 1 when USD or Token
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          dollarRate: 83, // Reset to default for INR
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handlePartyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const partyId = e.target.value;
    const partyIdNum = parseInt(partyId);
    const party = parties.find(p => p.id === partyIdNum);
    setFormData((prev) => ({
      ...prev,
      partyId,
      partyName: party?.partyName || '',
      mobile: party?.mobileNo || '',
      address: party?.address || '',
      partyAccountId: '', // Reset party account when party changes
      partyAccount: '',
    }));
  };

  const handleTokenChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const tokenId = e.target.value;
    const tokenIdNum = parseInt(tokenId);
    const token = tokens.find(t => t.id === tokenIdNum);
    setFormData((prev) => ({
      ...prev,
      tokenId,
      tokenName: token?.tokenName || '',
    }));
  };

  const handlePaymentModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const paymentModeId = e.target.value;
    const paymentModeIdNum = parseInt(paymentModeId);
    const paymentMode = paymentModes.find(pm => pm.id === paymentModeIdNum);
    setFormData((prev) => ({
      ...prev,
      paymentModeId,
      paymentMode: paymentMode?.paymentModeName || '',
    }));
  };

  const handleAccountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const accountId = e.target.value;
    const accountIdNum = parseInt(accountId);
    
    // Get account name based on payIn type
    let accountName = '';
    
    if (formData.payIn === 'INR') {
      const account = accounts.find(a => a.id === accountIdNum);
      accountName = account?.accountName || '';
    } else if (formData.payIn === 'USD') {
      const account = dollarAccounts.find(a => a.id === accountIdNum);
      accountName = account?.accountName || '';
    } else if (formData.payIn === 'Token') {
      const token = tokens.find(t => t.id === accountIdNum);
      accountName = token?.tokenName || '';
    }
    
    setFormData((prev) => ({
      ...prev,
      fromAccountId: accountId,
      fromAccount: accountName,
    }));
  };

  const handlePartyAccountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const partyAccountId = e.target.value;
    const partyAccountIdNum = parseInt(partyAccountId);
    const partyAccount = partyAccounts.find(pa => pa.id === partyAccountIdNum);
    setFormData((prev) => ({
      ...prev,
      partyAccountId,
      partyAccount: getAccountDisplayLabel(partyAccount || {} as PartyAccount),
    }));
  };

  // Helper function to get account display label based on account type
  const getAccountDisplayLabel = (account: PartyAccount): string => {
    // Account type IDs: 1=Bank, 2=UPI, 3=QR Code, 4=Wallet
    switch (account.accountTypeId) {
      case 1: // Bank Account
        return `${account.bankName} - ${account.accountNo}`;
      case 2: // UPI
        return account.upI_ID || 'UPI';
      case 3: // QR Code
        return account.qrLabel || 'QR Code';
      case 4: // Wallet
        return account.walletLabel || 'Wallet';
      case 5: // Wallet Address
        return account.walletLabel || 'Wallet';
      default:
        return account.bankName || 'Account';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.partyId || !formData.tokenId || !formData.paymentModeId || !formData.fromAccountId) {
      alert('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    onSubmit(formData);
  };

  if (loading) {
    return <div className={styles.loading}>Loading form data...</div>;
  }

  return (
    <form className={`${styles.form} ${styles.sellForm}`} onSubmit={handleSubmit}>
      {/* Party Details Section */}
      <div className={`${styles.section} ${styles.sellSection}`}>
        <h3 className={styles.sectionTitle}>Party Details</h3>
        
        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label htmlFor="partyId" className={styles.label}>
              Party <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputWithButton}>
              <select
                id="partyId"
                name="partyId"
                value={formData.partyId}
                onChange={handlePartyChange}
                className={styles.select}
                required
              >
                <option value="">Select party</option>
                {parties.map((party) => (
                  <option key={party.id} value={party.id}>
                    {party.partyName}
                  </option>
                ))}
              </select>
              <button type="button" className={styles.addButton} title="Add new party">
                +
              </button>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="date" className={styles.label}>
              Date <span className={styles.required}>*</span>
            </label>
            <input
              id="date"
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className={styles.input}
              required
            />
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label htmlFor="mobile" className={styles.label}>Mobile</label>
            <input
              id="mobile"
              type="text"
              name="mobile"
              value={formData.mobile}
              className={styles.input}
              disabled
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="address" className={styles.label}>Address</label>
            <input
              id="address"
              type="text"
              name="address"
              value={formData.address}
              className={styles.input}
              disabled
            />
          </div>
        </div>
      </div>

      {/* Token & Amount Section */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Token & Amount</h3>
        
        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label htmlFor="payIn" className={styles.label}>
              Payment In <span className={styles.required}>*</span>
            </label>
            <select
              id="payIn"
              name="payIn"
              value={formData.payIn}
              onChange={handleChange}
              className={styles.select}
              required
            >
              <option value="INR">INR</option>
              <option value="USD">USD</option>
              <option value="Token">Token</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="tokenId" className={styles.label}>
              Token <span className={styles.required}>*</span>
            </label>
            <select
              id="tokenId"
              name="tokenId"
              value={formData.tokenId}
              onChange={handleTokenChange}
              className={styles.select}
              required
            >
              <option value="">Select token</option>
              {tokens.map((token) => (
                <option key={token.id} value={token.id}>
                  {token.tokenName} ({token.symbol})
                </option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="quantity" className={styles.label}>
              Sell Qty <span className={styles.required}>*</span>
            </label>
            <input
              id="quantity"
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              className={styles.input}
              step="0.00000001"
              min="0"
              placeholder="0"
              required
            />
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label htmlFor="tokenRate" className={styles.label}>
              Token to $ Rate
            </label>
            <input
              id="tokenRate"
              type="number"
              name="tokenRate"
              value={formData.tokenRate}
              onChange={handleChange}
              className={styles.input}
              step="0.01"
              min="0"
              placeholder="1"
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="dollarRate" className={styles.label}>
              $ to INR Rate
            </label>
            <input
              id="dollarRate"
              type="number"
              name="dollarRate"
              value={formData.dollarRate}
              onChange={handleChange}
              className={styles.input}
              step="0.01"
              min="0"
              placeholder="83"
              disabled={formData.payIn === 'USD' || formData.payIn === 'Token'}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="total" className={styles.label}>
              Total Amount
            </label>
            <input
              id="total"
              type="number"
              name="total"
              value={formData.total.toFixed(2)}
              className={`${styles.input} ${styles.totalAmountSell}`}
              disabled
            />
          </div>
        </div>
      </div>

      {/* Payment Details Section */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Payment Details</h3>
        
        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label htmlFor="paymentModeId" className={styles.label}>
              Payment Mode <span className={styles.required}>*</span>
            </label>
            <select
              id="paymentModeId"
              name="paymentModeId"
              value={formData.paymentModeId}
              onChange={handlePaymentModeChange}
              className={styles.select}
              required
            >
              <option value="">Select mode</option>
              {paymentModes.map((mode) => (
                <option key={mode.id} value={mode.id}>
                  {mode.paymentModeName}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="fromAccountId" className={styles.label}>
              Receive To (Our Account) <span className={styles.required}>*</span>
            </label>
            <select
              id="fromAccountId"
              name="fromAccountId"
              value={formData.fromAccountId}
              onChange={handleAccountChange}
              className={styles.select}
              required
            >
              <option value="">Select account</option>
              {formData.payIn === 'INR' && accounts.map((account) => (
                <option key={account.id} value={account.id?.toString()}>
                  {account.accountName}
                </option>
              ))}
              {formData.payIn === 'USD' && dollarAccounts.map((account) => (
                <option key={account.id} value={account.id?.toString()}>
                  {account.accountName}
                </option>
              ))}
              {formData.payIn === 'Token' && tokens.map((token) => (
                <option key={token.id} value={token.id?.toString()}>
                  {token.tokenName} ({token.symbol})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label htmlFor="partyAccountId" className={styles.label}>
              Receive From (Party Account)
            </label>
            <div className={styles.inputWithButton}>
              <select
                id="partyAccountId"
                name="partyAccountId"
                value={formData.partyAccountId}
                onChange={handlePartyAccountChange}
                className={styles.select}
                disabled={!formData.partyId}
              >
                <option value="">Select party account</option>
                {filteredPartyAccounts.map((account) => (
                  <option key={account.id} value={account.id?.toString()}>
                    {getAccountDisplayLabel(account)}
                  </option>
                ))}
              </select>
              <button type="button" className={styles.addButton} title="Add party account">
                +
              </button>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="referenceNo" className={styles.label}>
              Reference No.
            </label>
            <input
              id="referenceNo"
              type="text"
              name="referenceNo"
              value={formData.referenceNo}
              onChange={handleChange}
              className={styles.input}
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="paymentDate" className={styles.label}>
            Payment Date
          </label>
          <input
            id="paymentDate"
            type="date"
            name="paymentDate"
            value={formData.paymentDate}
            onChange={handleChange}
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="remarks" className={styles.label}>
            Remarks
          </label>
          <textarea
            id="remarks"
            name="remarks"
            value={formData.remarks}
            onChange={handleChange}
            className={styles.textarea}
            rows={3}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className={styles.actions}>
        <button type="button" onClick={onCancel} className={styles.cancelButton} disabled={isSubmitting}>
          Cancel
        </button>
        <button type="submit" className={styles.saveSellButton} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Sell'}
        </button>
      </div>
    </form>
  );
};

export default AddSellTransactionForm;
